import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowRight, Globe } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useLang } from "@/i18n";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { t, lang, setLang } = useLang();

  const LINKS = [
    { to: "/", label: t("nav.home"), id: "accueil" },
    { to: "/produits", label: t("nav.products"), id: "produits" },
    { to: "/a-propos", label: t("nav.about"), id: "a-propos" },
    { to: "/temoignages", label: t("nav.testimonials"), id: "temoignages" },
    { to: "/faq", label: t("nav.faq"), id: "faq" },
    { to: "/contact", label: t("nav.contact"), id: "contact" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const LangToggle = ({ mobile = false }) => (
    <button
      data-testid={mobile ? "lang-toggle-mobile" : "lang-toggle"}
      onClick={() => setLang(lang === "fr" ? "en" : "fr")}
      aria-label={lang === "fr" ? "Switch to English" : "Passer au français"}
      className="group inline-flex items-center gap-1.5 rounded-full border border-slate-600 px-3.5 py-2 text-xs font-bold tracking-wider text-slate-200 transition-all duration-300 hover:border-sky-400/60 hover:text-sky-300"
    >
      <Globe size={13} className="text-sky-400" />
      {t("lang.label")}
    </button>
  );

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#05080E]/85 backdrop-blur-xl backdrop-saturate-150 border-b border-sky-500/10 py-3"
          : "bg-transparent py-5"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 sm:px-8 lg:px-16">
        <Link to="/" data-testid="nav-logo-link" aria-label="NordiGuard">
          <Logo />
        </Link>

        <nav className="hidden items-center gap-7 lg:flex" aria-label="Navigation principale">
          {LINKS.map((l) => (
            <NavLink
              key={l.id}
              to={l.to}
              data-testid={`nav-link-${l.id}`}
              className={({ isActive }) =>
                `text-sm font-medium tracking-wide transition-colors duration-300 hover:text-sky-400 ${
                  isActive ? "text-sky-400" : "text-slate-300"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <LangToggle />
          <Link
            to="/produits"
            data-testid="nav-cta-models"
            className="group inline-flex items-center gap-2 rounded-full bg-sky-400 px-5 py-2.5 text-sm font-semibold text-slate-950 transition-all duration-300 hover:bg-sky-300 hover:shadow-[0_0_28px_rgba(56,189,248,0.45)]"
          >
            {t("nav.cta")}
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>

        <div className="flex items-center gap-2.5 lg:hidden">
          <LangToggle mobile />
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-sky-500/20 text-slate-200"
            aria-label="Menu"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-b border-sky-500/10 bg-[#05080E]/95 backdrop-blur-xl lg:hidden"
            aria-label="Navigation mobile"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {LINKS.map((l) => (
                <NavLink
                  key={l.id}
                  to={l.to}
                  data-testid={`nav-mobile-link-${l.id}`}
                  className={({ isActive }) =>
                    `rounded-xl px-4 py-3 text-base font-medium transition-colors ${
                      isActive ? "bg-sky-500/10 text-sky-400" : "text-slate-300 hover:bg-slate-800/50"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/produits"
                data-testid="nav-mobile-cta-models"
                className="mt-3 inline-flex items-center justify-center gap-2 rounded-full bg-sky-400 px-5 py-3 text-sm font-semibold text-slate-950"
              >
                {t("nav.cta")} <ArrowRight size={16} />
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
